import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LifeBuoy, Search } from "lucide-react";

const S = ({ title, children }) => (
  <div className="mb-4">
    <div className="text-xs font-semibold text-slate-800 mb-1.5">{title}</div>
    <div className="text-xs text-slate-600 leading-relaxed space-y-1.5">{children}</div>
  </div>
);
const Ol = ({ items }) => (
  <ol className="list-decimal ml-4 space-y-1">{items.map((x, i) => <li key={i}>{x}</li>)}</ol>
);
const Ul = ({ items }) => (
  <ul className="list-disc ml-4 space-y-1">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
);
const B = ({ children }) => <span className="font-semibold text-slate-800">{children}</span>;

const GUIDES = [
  {
    id: "compliance-guards",
    title: "Compliance Guards: WWCC + Key Client Compliance",
    roles: ["Executive", "Admin", "BU Manager"],
    where: "HSEQ & Sustainability → Client Compliance",
    keywords: "compliance guard wwcc fm school licence certification induction red assignment aroflo tradesperson ntbel police client rules matrix",
    body: (
      <>
        <div className="mb-3">
          <a href="/downloads/gbe-compliance-guards-guide.pdf" target="_blank" rel="noreferrer"
             data-testid="compliance-guide-pdf-link"
             className="inline-flex items-center gap-1.5 text-xs font-semibold text-gbe underline underline-offset-2">
            Download the printable PDF guide
          </a>
        </div>
        <S title="What the guards do">
          <p>Two guards watch every <B>open AroFlo task</B> the moment assignments sync in (after every AroFlo sync, plus a nightly safety net):</p>
          <Ul items={[
            <><B>WWCC Guard</B> — anyone assigned to a task at a WWCC-required client (schools, preschools, Diocese) must hold a current Working With Children Check or exemption in the HR register</>,
            <><B>Key Client Compliance Guard</B> — per key client you define required <B>licences, certifications, inductions and/or the NSW FM list</B>. An employee missing any of them (or holding an expired one) is <B>RED — cannot be assigned</B> for that client</>,
          ]} />
          <p>Important: AroFlo has no mechanism to physically block an assignment, so the control is <B>detect fast + alert + record</B> — a non-compliant assignment survives at most one sync cycle (typically under an hour) before the right people are told to reassign.</p>
        </S>
        <S title="Setting up the Key Client Compliance Guard">
          <Ol items={[
            <>Go to <B>HSEQ → Client Compliance → Key client requirements</B></>,
            <>Search the client (both CRM and AroFlo client names are searchable) and select it</>,
            <>Add requirements: pick a source (<B>Licence / Certification / Induction / FM list</B>) then the exact item from your live register (e.g. "Electrical Licence", "Ausgrid L2 Authorisation")</>,
            <>Click <B>Save rules</B> — the employee matrix appears immediately showing who is RED (and exactly why: missing vs expired) and who is OK</>,
          ]} />
        </S>
        <S title="NSW school sites (Infrastructure FM list)">
          <Ol items={[
            <>Download the <B>TradesPersonList CSV</B> from NSW Infrastructure FM Web</>,
            <>Click <B>Upload FM list (CSV)</B> in the Client Compliance tab — a person is compliant only when <B>NTBEL + Police + WWCC are all "Yes"</B>, they're active and their licence is current</>,
            <>Toggle which clients count as school-FM sites in the <B>School-FM clients</B> list at the bottom</>,
            <>Re-upload whenever the FM portal list changes — the roster is replaced wholesale, so it's always current</>,
          ]} />
        </S>
        <S title="What happens when someone non-compliant is assigned">
          <Ul items={[
            <>The task appears under <B>Flagged assignments</B> with the exact reasons (e.g. "Electrical Licence: expired 2026-05-01; Ausgrid L2: missing")</>,
            <>The <B>worker is emailed</B>: do not attend — and their <B>BU manager</B> gets the same email (executives if no manager is set)</>,
            <>Executives get an in-hub critical notification</>,
            <>The flag <B>auto-resolves</B> when the task is reassigned/closed or the compliance record is fixed; you can also resolve manually with a note</>,
          ]} />
        </S>
        <S title="WWCC Guard specifics">
          <Ul items={[
            <>The WWCC Guard sits in the <B>same Client Compliance tab</B>, below the key-client rules — toggle which clients require a WWCC there; child-related names are auto-suggested for review</>,
            <>WWCC status comes from the HR inductions register ("WWCC" / "Working with Children" / exemption rows) — expiring-within-30-days is flagged as a warning</>,
          ]} />
        </S>
        <S title="Keeping the data honest">
          <Ul items={[
            <>The guards are only as good as the <B>Compliance Register import</B> — keep the weekly SharePoint register import running (Import Hub)</>,
            <>Use <B>Run check now</B> after fixing a record to clear flags immediately instead of waiting for the next sync</>,
            <>If a person shows RED unexpectedly, check name spelling matches between AroFlo and the register — unmatched names are listed on the HR page</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "remuneration",
    title: "Updating remuneration history, review dates & comments",
    roles: ["Executive", "Admin", "BU Manager"],
    where: "HR & Workforce → Remuneration",
    keywords: "pay salary review remuneration history dates comments operations register import payroll myob benchmark budget rewards allowances",
    body: (
      <>
        <div className="mb-3">
          <a href="/downloads/gbe-remuneration-guide.pdf" target="_blank" rel="noreferrer"
             data-testid="remuneration-guide-pdf-link"
             className="inline-flex items-center gap-1.5 text-xs font-semibold text-gbe underline underline-offset-2">
            Download the printable PDF guide (how it works, data sources & managing)
          </a>
        </div>
        <S title="Recording a pay change or review">
          <Ol items={[
            <>Click <B>New review</B> at the top of the Remuneration section</>,
            <>Fill in: Employee → Basis (salary/hourly) → <B>Effective date</B> → new salary or hourly rate → standard weekly hours</>,
            <>Set <B>Status</B>: Draft (still working on it), Submitted / Under review (needs sign-off), or Approved (takes effect immediately)</>,
            <>Add the <B>Reason / notes</B> — your comment, kept on the record permanently</>,
          ]} />
          <p>Saving creates a <B>dated transaction — history is never overwritten</B>, so past pay records stay intact.</p>
        </S>
        <S title="Updating review dates">
          <Ul items={[
            <>The <B>Next review due</B> date is automatic: 12 months from the latest approved record's effective date</>,
            <>To correct or move a review date, record a <B>new review with the correct effective date</B> — the next-due date recalculates</>,
            <>The <B>Pending Reviews</B> and <B>Reviews Due</B> sub-tabs show what needs action; overdue items are flagged</>,
          ]} />
        </S>
        <S title="Adding comments">
          <Ul items={[
            <>On a transaction: the <B>Reason / notes</B> field when creating it</>,
            <>On an approval decision: in Pending Reviews use Approve / Reject / Info — each opens a comment box kept on the record</>,
            <>Click an employee to open their profile: full remuneration timeline (immutable), allowances, and a <B>Change history</B> expander per record showing who, when and comments</>,
            <>Everything is also logged under the <B>Audit</B> sub-tab</>,
          ]} />
        </S>
        <S title="Fixing historical records">
          <p><B>Never edit — add.</B> Record a new dated transaction with the correct figures and explain in the notes. The timeline preserves both entries so the audit trail stays honest.</p>
        </S>
      </>
    ),
  },
  {
    id: "rewards",
    title: "Rewards history & recording new rewards",
    roles: ["Executive", "Admin", "BU Manager"],
    where: "HR & Workforce → Remuneration → Rewards",
    keywords: "rewards recognition issue distribute operations",
    body: (
      <>
        <S title="Recording a reward as it's distributed">
          <Ol items={[
            <>Click <B>Issue reward</B></>,
            <>Select the employee, the reward details/value, date and any notes</>,
            <>Save — it's recorded against the employee so recognition stays fairly distributed</>,
          ]} />
        </S>
        <S title="Keeping the history healthy">
          <Ul items={[
            <>The Rewards panel shows a <B>distribution summary</B> plus the 25 most recent rewards — check it before issuing to spot anyone over- or under-recognised</>,
            <>Record rewards <B>at the time they're given</B> (dated transactions, same as pay records) so month-end reporting stays accurate</>,
            <>Rewards appear on the employee's profile timeline alongside pay history, and in the Audit log</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "hr-admin",
    title: "Managing HR: induction links & escalation reminder emails",
    roles: ["Admin", "BU Manager"],
    where: "HR & Workforce → Inductions / Escalations",
    keywords: "induction url link catalogue escalation reminder email expiry administration manager mailbox",
    body: (
      <>
        <S title="Induction catalogue & URLs — Inductions tab">
          <Ol items={[
            <>Add a new induction: click <B>Add induction</B> → Name, Category, Provider, <B>Direct link to induction</B> (the URL workers click), Validity (months), Notes, Active</>,
            <>Add or fix a URL on an existing entry: click the <B>pencil (edit)</B> icon — entries without one show "No link yet"</>,
            <><B>Seed from register</B>: one click pulls every induction name already in the HR register into the catalogue — then edit each to add its link</>,
            <>Workers click "Go to induction", complete it externally, then add it to their record — it lands as <B>pending HR verification</B>: use Verify ✓ or reject ✗</>,
          ]} />
          <p>Deleting a catalogue entry never deletes workers' existing induction records.</p>
        </S>
        <S title="Escalation reminder emails — Escalations tab">
          <Ol items={[
            <>Per category (Licences, Certifications, Inductions, Contracts): switch on/off and tick reminder periods — <B>2 months, 1 month, 2 weeks, 1 week</B> before expiry (automatically daily in the final week)</>,
            <>Recipients per category: the employee, their manager, any <B>extra emails</B> (comma-separated), and optionally the inductions mailbox</>,
            <>Set the shared <B>inductions mailbox</B> address and Save</>,
            <>Manager mapping: in the employee list, search a name and enter their <B>manager's email</B> — that's who receives their escalations</>,
            <><B>Send now</B> fires today's reminder run immediately — useful to confirm delivery after changing settings</>,
          ]} />
          <p>The log at the bottom shows every reminder sent (also visible in Resend).</p>
        </S>
      </>
    ),
  },
  {
    id: "task-audits",
    title: "BU Managers: monthly Task JSA & CCEW audits",
    roles: ["BU Manager", "Executive", "Admin"],
    where: "HSEQ & Sustainability → Task Audits (JSA/CCEW)",
    keywords: "jsa ccew audit sample task monthly bu manager checklist safety",
    body: (
      <>
        <S title="What this is">
          <p>Each month the hub randomly selects <B>10% of the tasks your field workers completed</B> (spread across as many different workers as possible). Your job: verify each sampled task's safety paperwork. This replaces the old audit spreadsheet.</p>
        </S>
        <S title="When">
          <p>You'll get an email — "Your audit sample is ready" — in the first few days of each month. Click the link or go to <B>HSEQ → Task Audits</B>. Aim to finish before month end; the hub reminds you from the 24th if any are still pending.</p>
        </S>
        <S title="Completing an audit (about a minute per task)">
          <Ol items={[
            <>Click <B>Audit</B> on a row in your BU's list</>,
            <>1. JSA carried out for this task? — Yes / No</>,
            <>2. JSA completed correctly — no shortcuts? (shown if JSA was done: look for copy-paste hazards, missing controls, after-the-fact signatures)</>,
            <>3. Does this job require a CCEW compliance form? — Yes / No</>,
            <>4. CCEW form completed? (shown if required)</>,
            <>Add <B>comments</B> — especially on fails, say what you saw — then <B>Save audit</B>. Pass/Fail is calculated automatically</>,
          ]} />
        </S>
        <S title="What a Fail triggers">
          <p>A task fails if the JSA wasn't done, was done with shortcuts, or a required CCEW form is missing. A fail automatically creates a <B>high-priority corrective action</B> (due in 14 days) under HSEQ → Corrective Actions and notifies leadership — nothing extra to raise manually, but follow the action through with the worker.</p>
        </S>
        <S title="Tips">
          <Ul items={[
            <>The progress cards show how many of your sample are done (e.g. "4/13 audited")</>,
            <>Partially answered audits stay Pending — come back anytime; use <B>Review</B> to adjust before month end</>,
            <>Results feed company QMS pass-rate reporting, so accuracy beats speed</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "field-capture",
    title: "Field workers: QR asset capture & updates",
    roles: ["Field Worker", "All staff"],
    where: "Scan the asset's QR label (or /a/TAG-ID)",
    keywords: "qr scan field asset tag photo condition rating capture mobile",
    body: (
      <>
        <S title="Scanning and updating an asset">
          <Ol items={[
            <>Scan the <B>QR label</B> on the asset with your phone camera — it opens the asset's field page (no app needed; log in with your hub account)</>,
            <>Check the asset details, then update what you observed: <B>Condition, Risk, Utilisation, Environment</B> (1–5), remaining life, install year, name or notes</>,
            <>Add <B>photos</B> straight from the camera — they attach to the asset record</>,
            <>Save — the asset is marked <B>field-validated</B> (data confidence High) with a full audit trail of who changed what</>,
          ]} />
        </S>
        <S title="What happens to your update">
          <Ul items={[
            <>Ratings and details sync <B>back to AroFlo automatically</B> — you'll see "Changes synced back to AroFlo" after saving</>,
            <>Field-validated values are protected: the nightly AroFlo sync will never overwrite them</>,
            <>If a label is missing or damaged, office staff can print replacements from Asset Intelligence → Renewal Assessment → Print QR labels</>,
          ]} />
        </S>
        <S title="Rules of thumb">
          <Ul items={[
            <>Scores are <B>1–5 where 1 = best, 5 = worst</B>; the app rejects anything else</>,
            <>Only change what you can verify onsite — unsure? Leave it and capture a photo</>,
            <>Wrong asset opens? The label was mis-fitted — don't update, report it for a re-label</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "qr-codes",
    title: "QR codes: asset, site & client",
    roles: ["Executive", "Admin", "BU Manager", "All staff"],
    where: "Asset Intelligence → Renewal Assessment",
    keywords: "qr code client site asset poster label print batch mail-out portfolio scan portal image png pdf",
    body: (
      <>
        <S title="Three levels of QR — all login-gated">
          <p>Every scan requires the person to <B>log in</B> (hub or client-portal account) and only shows data they're authorised for.</p>
          <Ul items={[
            <><B>Asset</B> — opens that asset's field page (condition, photos, field updates)</>,
            <><B>Site</B> — opens that site's live register (condition, renewal budget, jobs)</>,
            <><B>Client</B> — opens the client's whole portfolio: every site + totals</>,
          ]} />
        </S>
        <S title="Asset QR labels (per asset)">
          <Ol items={[
            <>Go to <B>Asset Intelligence → Renewal Assessment</B>; optionally pick a site</>,
            <>Click <B>Print QR labels</B> — a PDF of 62mm thermal labels, one per asset</>,
            <>Stick on the asset; scanning opens its field page for condition/photo updates</>,
          ]} />
        </S>
        <S title="Site QR posters (per site)">
          <Ul items={[
            <>Clients can self-serve in the <B>Client Portal → Site QR posters</B>: pick a site for a poster/QR image, or download an <B>"all sites" batch PDF</B></>,
            <>Scanning opens that single site's live asset intelligence</>,
          ]} />
        </S>
        <S title="Client QR — whole-of-client (new)">
          <Ol items={[
            <>In <B>Renewal Assessment</B>, first pick the client in the <B>top-right client selector</B></>,
            <><B>Client QR poster</B> → A4 poster PDF. Scanning opens the client's whole portfolio: total assets, condition mix, immediate + 12-month renewal budget, top asset classes, and a list of every site (tap to drill in)</>,
            <><B>QR image</B> → just the QR as a PNG (for slides, emails, signage)</>,
          ]} />
        </S>
        <S title="Batch client posters — for mail-outs (new)">
          <Ol items={[
            <>Click <B>Batch posters</B> in Renewal Assessment</>,
            <>Search and tick the clients you want (or <B>Select all shown</B>)</>,
            <>Click <B>Generate</B> → one PDF with a QR poster page per client; print and mail out</>,
          ]} />
        </S>
        <S title="Good to know">
          <Ul items={[
            <><B>Login required</B> — scanning never exposes data publicly; if a client can't open it, confirm they have a portal login for that organisation</>,
            <>QR codes are <B>stable</B> — reprinting a label/poster gives the same working code</>,
            <>If a label scan says "not found", it may be from an old batch — reprint from the hub</>,
          ]} />
        </S>
      </>
    ),
  },

  {
    id: "import-hub",
    title: "Import Hub: feeds, uploads & service keys",
    roles: ["Admin", "Executive"],
    where: "Import Hub",
    keywords: "import upload feed csv excel power automate service key history recipe stale",
    body: (
      <>
        <S title="Uploading data manually">
          <Ol items={[
            <>Open <B>Import Hub</B> and pick the recipe matching your file (e.g. Completed Tasks, Contracts, Employees)</>,
            <>Each recipe lists its expected columns with examples — <B>Preview</B> validates without saving, <B>Apply</B> imports</>,
            <>Row-level errors are reported with row numbers; valid rows still import. Re-uploading corrected files is always safe — rows update rather than duplicate</>,
          ]} />
        </S>
        <S title="Automated feeds (Power Automate)">
          <Ul items={[
            <>IT posts files/data to the hub using a <B>service key</B> (managed under Import Hub → Service Keys — keys can be limited to specific recipes)</>,
            <>Every arrival (or rejection) is logged under <B>History</B> — nothing vanishes silently</>,
            <><B>Feed Health</B> shows when each source last refreshed with stale warnings (daily/weekly/monthly cadence per feed), plus AroFlo write-back health with one-click retry</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "user-admin",
    title: "Creating users: General Admin & Field Worker accounts",
    roles: ["Executive", "Ops Management"],
    where: "Administration → Organisation → Users",
    keywords: "create user invite account general admin administration field worker onboarding password 2fa role login new starter",
    body: (
      <>
        <S title="Who can do this">
          <p>Users can be created by <B>Executive</B> and <B>Operations Management</B> accounts. Go to <B>Organisation → Users</B> (Administration section of the sidebar). You never type a password for anyone — the hub emails a secure invitation and the new user sets their own.</p>
        </S>
        <S title="Creating a General Administration user">
          <Ol items={[
            <>Click <B>Add user</B> (Send invitation)</>,
            <>Enter their <B>work email</B> and <B>full name</B></>,
            <>Role: choose <B>General Administration</B> — finance, HR and admin processing. No Business Unit needed (it's organisation-wide)</>,
            <>Click <B>Send invitation</B> — the email goes from <B>invites@gbehub.com.au</B> and the link is valid for <B>72 hours</B></>,
          ]} />
          <p>What they get: full <B>HR &amp; Workforce</B>, Operations and Sales editing, CRM and BU Performance viewing. They do <B>not</B> see Executive, Financial or Administration surfaces (Organisation, Import Hub, Integrations etc.). They land on the HR page after login.</p>
        </S>
        <S title="Creating a Field Worker">
          <Ol items={[
            <>Click <B>Add user</B> (Send invitation)</>,
            <>Enter their <B>email</B> and <B>full name</B></>,
            <>Role: choose <B>Field Worker</B>, then pick their <B>Business Unit</B> — this is required and controls what they see</>,
            <>Click <B>Send invitation</B></>,
          ]} />
          <p>Field workers see their assigned jobs and own records only, and can use <B>QR asset capture</B> in the field (scan a tag → update condition, photos and details).</p>
        </S>
        <S title="What the new user does">
          <Ol items={[
            <>Opens the invitation email and clicks the link (within 72 hours)</>,
            <>Sets their <B>own password</B></>,
            <>Enrols in <B>two-factor authentication</B> (authenticator app) — this is mandatory for everyone; the hub prompts them on first login</>,
          ]} />
        </S>
        <S title="If the link expires or the email doesn't arrive">
          <Ul items={[
            <>Pending invitations show at the top of the user list with an <B>"Invite sent"</B> badge</>,
            <><B>Resend</B> gives a fresh 72-hour link (and shows you a copyable invite URL if their mailbox is being difficult)</>,
            <><B>Revoke</B> cancels an invitation that should never be used</>,
          ]} />
        </S>
        <S title="Changing someone later">
          <p>Click the user in the list to edit their name, role, Business Unit or to <B>deactivate</B> them (they can no longer log in, but their history is kept). Every change is recorded in the Audit log.</p>
        </S>
      </>
    ),
  },
  {
    id: "site-qr-manager",
    title: "Site QR posters — printing & rollout (BU Managers)",
    roles: ["Executive", "Admin", "BU Manager"],
    where: "CRM → Sites · or · Client Portal → Sites & Assets",
    keywords: "site qr poster print rollout mount scan portal onsite client",
    body: (
      <>
        <S title="What it is">
          <p>Every client site can have a printed QR poster. Scanning it opens a <B>secure, live view of that site's asset register</B> — condition, renewal priorities, budget position and works at the site. Login required, so only authorised users see data.</p>
        </S>
        <S title="1 · Confirm access">
          <Ul items={[
            <>GBE field staff need a Hub account (<B>Organisation → Users</B>) — access is by invitation, no shared logins</>,
            <>Client site contacts (optional) need a <B>Client Portal</B> account — they only ever see their own sites</>,
            <>First login = set password + enrol two-factor (authenticator app) — allow 5 minutes per person</>,
          ]} />
        </S>
        <S title="2 · Print the poster">
          <Ul items={[
            <><B>One-off:</B> CRM → Sites tab → find the site → click <B>Poster</B> → A4 PDF downloads</>,
            <><B>Whole client:</B> Client Portal → select the client → Sites &amp; Assets tab → <B>Site QR posters</B> block → choose site → <B>Download A4 poster (PDF)</B> (or QR image only for custom labels)</>,
            <>Print A4 colour; laminate for plant rooms / outdoor spots</>,
          ]} />
        </S>
        <S title="3 · Mount & verify">
          <Ul items={[
            <>Best spots: plant room door, switchboard cupboard, site office noticeboard — eye level, out of weather</>,
            <>Before leaving, scan it yourself: login page appears → sign in → you land on that site's asset view</>,
            <>If the site shows 0 assets, the register isn't loaded — raise with the Asset Intelligence team first</>,
          ]} />
        </S>
        <S title="Ongoing">
          <p>Posters never expire — they always show live data. Damaged? Reprint, same QR. Clients can download their own posters from their portal.</p>
        </S>
      </>
    ),
  },
  {
    id: "site-qr-field",
    title: "Using Site QR posters onsite (Field staff & clients)",
    roles: ["All roles"],
    where: "Scan the poster → /site view",
    keywords: "site qr scan phone login asset view condition works mobile",
    body: (
      <>
        <S title="How to use it">
          <Ol items={[
            <><B>Scan</B> the GBE QR poster with your phone camera — no app needed</>,
            <><B>Sign in</B> with your Hub email + password + 6-digit authenticator code</>,
            <>You land on the <B>site view</B>: asset count, immediate &amp; 12-month renewal budgets, condition bar (green = good, amber = fair, red = poor), works at this site, and the full asset list sorted worst-first</>,
            <><B>Tap any asset</B> for detail: condition, health, priority, install year, remaining life, forecast renewal and estimated cost</>,
          ]} />
        </S>
        <S title="Troubleshooting">
          <Ul items={[
            <><B>"This site does not belong to your organisation"</B> — you're logged in with a client account for a different client; use your GBE staff login</>,
            <>Forgotten password → use <B>Forgot password</B> on the sign-in page</>,
            <>QR won't scan (damage/glare) → ask your BU manager to reprint it</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "asset-qr-labels",
    title: "Asset QR labels — printing & fitting (BU Managers)",
    roles: ["Executive", "Admin", "BU Manager"],
    where: "Asset Intelligence → Renewal Assessment → Print QR labels",
    keywords: "asset qr label print brother thermal sticker fit nameplate renewal",
    body: (
      <>
        <S title="What it is">
          <p>Every asset gets its own QR sticker on the asset itself. Scanning opens that asset's live record where techs <B>validate and update data on the spot</B> — updates write back to AroFlo and lift data confidence to High.</p>
        </S>
        <S title="1 · Printer & consumables">
          <Ul items={[
            <>Labels are formatted for a <B>Brother DK thermal printer, 62 × 29 mm rolls</B> — one asset per label</>,
            <>Use durable/laminated DK film rolls for plant rooms and external units</>,
          ]} />
        </S>
        <S title="2 · Print">
          <Ol items={[
            <>Asset Intelligence → <B>Renewal Assessment</B> tab</>,
            <>Select the <B>Client</B>, then the <B>Site</B> filter</>,
            <>Click <B>Print QR labels (site)</B> — PDF downloads, one label per asset</>,
            <>Print at 100% scale (no "fit to page")</>,
          ]} />
          <p>Leaving site on "All" prints every asset for the client (max 500) — go site-by-site so rolls arrive grouped per site.</p>
        </S>
        <S title="3 · Fit & verify">
          <Ul items={[
            <>Stick on a clean, flat, accessible face — near the nameplate is ideal</>,
            <>Avoid hot surfaces, painted door edges, ladder-only positions</>,
            <><B>Match the label to the physical asset before sticking</B> — a wrong label is worse than none</>,
            <>Scan 2–3 fitted labels before leaving to confirm the right records open</>,
          ]} />
        </S>
        <S title="Rollout tip">
          <p>Fold labelling into existing work — give each tech the label roll for sites they're already attending that week.</p>
        </S>
      </>
    ),
  },
  {
    id: "hr-workforce-admin",
    title: "HR & Workforce administration (HR Admin)",
    roles: ["Executive", "Admin", "HR Admin"],
    where: "HR Hub · Import Hub",
    keywords: "hr workforce employees licences certifications inductions compliance wwcc expiry remuneration import",
    body: (
      <>
        <S title="Daily view — HR Hub">
          <Ul items={[
            <><B>Overview</B>: workforce counts, expiring licences/certs at a glance</>,
            <><B>Employees</B>: the workforce list — roles, BUs, contact details</>,
            <><B>Licences / Certifications / Inductions</B>: each table has a <B>search box</B> (employee, type, number/client) with a live result count — e.g. type "wwcc" to see every Working With Children Check holder. "No expiry" is shown for checks that never lapse</>,
          ]} />
        </S>
        <S title="Keeping compliance data current">
          <Ul items={[
            <>Data loads from the <B>Compliance Register</B> export via <B>Import Hub</B> — upload the raw spreadsheet, the recipe maps it automatically, and re-uploads upsert (never duplicate)</>,
            <>Expiring items trigger automatic <B>escalation reminder emails</B> — see the "Managing HR" guide for induction links &amp; reminders</>,
            <>WWCC/exemptions feed the <B>WWCC Guard</B> in HSEQ — keep them current or field staff get stop-work flags</>,
          ]} />
        </S>
        <S title="Remuneration (restricted)">
          <p>The <B>Remuneration</B> tab is visible to Executive, Admin and BU Manager roles only — pay history, review dates and comments. See the dedicated remuneration guide.</p>
        </S>
      </>
    ),
  },
  {
    id: "feedback-csat-admin",
    title: "Feedback & CSAT administration",
    roles: ["Admin", "Ops", "BU Manager", "Executive"],
    where: "Feedback page",
    keywords: "feedback csat compliments complaints queries survey pulse resolve email intake action register",
    body: (
      <>
        <S title="Where feedback comes from">
          <Ul items={[
            <><B>Email intake</B>: client emails routed via Power Automate are AI-classified into compliments / complaints / queries automatically</>,
            <><B>Action Register sync</B>: SharePoint Action Register rows categorised Complaints or Compliments mirror into the Feedback register on every import — no double entry</>,
            <><B>Manual</B>: click <B>Log feedback</B> on the Feedback page for anything phoned in or mentioned onsite</>,
          ]} />
        </S>
        <S title="Working the register">
          <Ol items={[
            <>Use the tabs — <B>All / Compliments / Complaints / Queries / Open only</B></>,
            <>Open items age until resolved — click <B>Resolve</B> once the client has been closed out</>,
            <>Complaints feed each BU's scorecard (complaints received / outstanding / resolution days), so resolve promptly and honestly</>,
          ]} />
        </S>
        <S title="CSAT — Customer Pulse tab">
          <Ul items={[
            <><B>Send all due</B> emails the CSAT survey to eligible client contacts; the <B>auto-send</B> toggle does it automatically after each import</>,
            <>Clients scoring 1–2 are told a manager will follow up <B>within one business day</B> — the low-score alert email tells you who to call</>,
            <><B>Account reviews</B>: internal contract reviews are logged in the same tab for CSAT context</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "hseq-sustainability-admin",
    title: "HSEQ & Sustainability administration",
    roles: ["Admin", "HSEQ", "BU Manager", "Executive"],
    where: "HSEQ page",
    keywords: "hseq incidents trifr lti safety audits task audits corrective actions wwcc sustainability emissions energy water waste ampol scope 3",
    body: (
      <>
        <S title="Safety side">
          <Ul items={[
            <><B>Incident Register</B>: log incidents/near misses directly, or bulk-load the raw SharePoint Incident/Injury Register via Import Hub (classification, LTI hours and periods are derived automatically). Feeds TRIFR/LTIFR</>,
            <><B>Task Audits (JSA/CCEW)</B>: monthly worker-day samples for BU managers to audit — see the dedicated task-audit guide</>,
            <><B>Corrective Actions</B>: track actions from incidents to closure</>,
            <><B>WWCC Guard</B>: flags workers assigned to child-related sites without a valid check and emails them + their manager. Toggle which clients require a WWCC and resolve flags here</>,
          ]} />
        </S>
        <S title="Sustainability side">
          <Ul items={[
            <>Switch to the <B>Sustainability Site</B> view for the environmental tabs: <B>Emissions · Energy · Water · Waste · Habitat · People · Fleet</B></>,
            <><B>Ampol tab</B>: monthly fuel data arrives by feed or the import dialog — powers fleet emissions and the Driver League</>,
            <><B>Bills Inbox</B>: drop energy/water bills in for extraction into the metrics</>,
            <><B>Client Scope 3</B>: per-client emissions attribution with downloadable certificates for client reporting</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "fleet-vehicles-admin",
    title: "Fleet & vehicle management",
    roles: ["Admin", "Ops", "Executive"],
    where: "Vehicles page",
    keywords: "fleet vehicles rego driver odometer service overdue ampol fuel",
    body: (
      <>
        <S title="What the page shows">
          <Ul items={[
            <>Full fleet list — <B>rego, vehicle, type, driver, base, odometer, last service</B> — sortable and searchable</>,
            <><B>Service compliance</B>: each vehicle shows days since last service with a progress bar; anything 6 months+ shows in the <B>Service Overdue</B> KPI and turns red</>,
            <>Driver assignments come from the monthly fleet report feed — keep that feed current and the page stays current</>,
          ]} />
        </S>
        <S title="Admin routine">
          <Ol items={[
            <>Weekly: scan the <B>Service Overdue</B> KPI and book anything red</>,
            <>Monthly: confirm the fleet report and Ampol fuel imports have landed (Import Hub → feed health shows if a feed goes quiet — IT is auto-alerted)</>,
            <>Fuel usage per vehicle/driver flows to Sustainability → Fleet &amp; Driver League automatically</>,
          ]} />
        </S>
      </>
    ),
  },
  {
    id: "contracts-admin",
    title: "Contracts register administration",
    roles: ["Admin", "Executive", "BU Manager"],
    where: "Contracts page",
    keywords: "contracts register renewal expiry expiring 30 90 days client value new contract",
    body: (
      <>
        <S title="Managing the register">
          <Ol items={[
            <>Click <B>New contract</B> to add one — client, dates, value, terms</>,
            <>Statuses are automatic from the end date: <B>Expired</B> (red), <B>≤ 30 days</B> (amber), <B>≤ 90 days</B> (yellow), current (green)</>,
            <>Use the <B>expiring-only</B> filter for renewal planning — everything inside 90 days in one list</>,
          ]} />
        </S>
        <S title="Admin routine">
          <Ul items={[
            <>Monthly: review the ≤ 90-day list and start renewal conversations before the amber window</>,
            <>Keep values and end dates current — the register drives the renewal pipeline leadership sees</>,
            <>Negotiating? Pair the register with the Client Portal value-add pack (Site QR, Asset Intelligence, Scope 3) to strengthen renewals</>,
          ]} />
        </S>
      </>
    ),
  },
];

export default function HelpPage() {
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const guides = GUIDES.filter((g) => !ql ||
    g.title.toLowerCase().includes(ql) || g.keywords.includes(ql) ||
    g.where.toLowerCase().includes(ql) || g.roles.join(" ").toLowerCase().includes(ql));

  return (
    <AppLayout title="Help & How-to" subtitle="Step-by-step guides for day-to-day hub tasks">
      <div className="max-w-3xl space-y-4" data-testid="help-page">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search"
                 placeholder="Search guides… (e.g. rewards, escalation, induction, audit)"
                 className="pl-9 h-9 text-sm" />
        </div>
        {guides.length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-10">No guides match — try a different word.</div>
        ) : (
          <Accordion type="multiple" className="space-y-3">
            {guides.map((g) => (
              <AccordionItem key={g.id} value={g.id}
                             className="border border-slate-200 rounded-lg bg-white px-4 data-[state=open]:shadow-sm">
                <AccordionTrigger data-testid={`help-guide-${g.id}`} className="hover:no-underline py-3">
                  <div className="flex items-start gap-3 text-left">
                    <LifeBuoy className="w-4 h-4 text-gbe mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{g.title}</div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[10px] text-slate-500">{g.where}</span>
                        {g.roles.map((r) => (
                          <Badge key={r} variant="outline" className="text-[9px] px-1.5 py-0 text-slate-500">{r}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-1">{g.body}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </AppLayout>
  );
}
